import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UserAppDraft } from '@api/appBuilder';
import type { AppRunDetail } from '@api/apps';
import type { AutostartRequest, RunHost } from '../../appFrame/runHost';
import { manifestProblemsOf } from '../appBuilderErrors';
import { useAppBuilderActions } from '../useAppBuilder';
import { BUILDER_RUN_PARAM } from './builderWorkspace.constants';

interface PendingStart {
  readonly runId: string;
  readonly request: AutostartRequest;
}

// What to start a run with lives in memory only, so a reload shows the run but never starts one.
export const useBuilderRunHost = (
  draft: UserAppDraft,
  onProblems: (problems: readonly string[]) => void,
): RunHost => {
  const [params, setParams] = useSearchParams();
  const runId = params.get(BUILDER_RUN_PARAM) ?? undefined;
  const [pending, setPending] = useState<PendingStart | null>(null);
  const { startTestRun } = useAppBuilderActions();
  const { appKey, preview } = draft;

  const withRun = useCallback(
    (id: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) next.set(BUILDER_RUN_PARAM, id);
          else next.delete(BUILDER_RUN_PARAM);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const autostartFor = useCallback(
    (id: string) => (pending?.runId === id ? pending.request : null),
    [pending],
  );

  const showRun = useCallback(
    (id: string, request: AutostartRequest) => {
      setPending({ runId: id, request });
      withRun(id);
    },
    [withRun],
  );

  const startOver = useCallback(() => {
    setPending(null);
    withRun(null);
  }, [withRun]);

  const openRun = useCallback(async (): Promise<AppRunDetail> => {
    onProblems([]);
    try {
      return await startTestRun(appKey);
    } catch (error) {
      const problems = manifestProblemsOf(error);
      if (problems.length > 0) onProblems(problems);
      throw error;
    }
  }, [appKey, onProblems, startTestRun]);

  const openTranscript = useCallback((run: AppRunDetail) => {
    window.open(`/conversation/${encodeURIComponent(run.conversationId)}`, '_blank', 'noopener');
  }, []);

  return useMemo<RunHost>(
    () => ({
      appKey,
      runId,
      composeManifest: preview,
      composeLoading: false,
      composeCrossOrg: false,
      testRuns: true,
      setupTarget: 'draft',
      autostartFor,
      openRun,
      showRun,
      startOver,
      openTranscript,
    }),
    [appKey, autostartFor, openRun, openTranscript, preview, runId, showRun, startOver],
  );
};
